import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixDynamicPageRouterV1DynamicPageRouterUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'editor._base_domain_': [
      {
        srcPath: '/_api/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    'blocks._base_domain_': [
      {
        srcPath: '/_api/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    'create.editorx': [
      {
        srcPath: '/_api/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    _: [
      {
        srcPath: '/_api/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    'dev._base_domain_': [
      {
        srcPath: '/_api/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    'api._api_base_domain_': [
      {
        srcPath: '/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/dynamic-page-router',
        destPath: '',
      },
    ],
    'www._base_domain_': [
      {
        srcPath: '/_api/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    'bo._base_domain_': [
      {
        srcPath: '/_api/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    'wixbo.ai': [
      {
        srcPath: '/_api/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    'wix-bo.com': [
      {
        srcPath: '/_api/dynamic-pages-router',
        destPath: '',
      },
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    'manage._base_domain_': [
      {
        srcPath: '/_api/dynamic-page-router',
        destPath: '',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/dynamic-page-router/v1/pages',
        destPath: '/v1/pages',
      },
      {
        srcPath: '/dynamic-page-router/v1/sitemap',
        destPath: '/v1/sitemap',
      },
      {
        srcPath: '/dynamic-page-router/v1/draft-preview-token',
        destPath: '/v1/draft-preview-token',
      },
    ],
    '*.dev.wix-code.com': [
      {
        srcPath: '/dynamic-page-router/v1/pages',
        destPath: '/v1/pages',
      },
      {
        srcPath: '/dynamic-page-router/v1/sitemap',
        destPath: '/v1/sitemap',
      },
      {
        srcPath: '/dynamic-page-router/v1/draft-preview-token',
        destPath: '/v1/draft-preview-token',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_dynamic-page-router_pages';

/**
 * Returns the count of sitemap entries grouped by page name.
 *
 * Use this endpoint for analytics dashboards, capacity planning, and understanding
 * the scale of dynamic content without retrieving actual URLs. Results are capped
 * at 1,000 items per pattern for performance.
 */
export function getRouterSitemapCount(
  payload: object
): RequestOptionsFactory<any> {
  function __getRouterSitemapCount({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.dynamic_page_router.v1.page',
      method: 'POST' as any,
      methodFqn:
        'wix.dynamic_page_router.v1.DynamicPageRouter.GetRouterSitemapCount',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixDynamicPageRouterV1DynamicPageRouterUrl({
        protoPath: '/v1/sitemapCount',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __getRouterSitemapCount;
}

/**
 * Returns sitemap entries containing URLs, page names, and page titles.
 *
 * Use this endpoint to generate XML sitemaps for search engines, build navigation
 * menus, or provide URL listings to external systems. The `fullUrl` parameter
 * provides context for scoping sitemap generation to specific domains or URL hierarchies.
 */
export function getRouterSitemap(payload: object): RequestOptionsFactory<any> {
  function __getRouterSitemap({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.dynamic_page_router.v1.page',
      method: 'POST' as any,
      methodFqn:
        'wix.dynamic_page_router.v1.DynamicPageRouter.GetRouterSitemap',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixDynamicPageRouterV1DynamicPageRouterUrl({
        protoPath: '/v1/sitemap',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [{ path: 'result.priority' }],
          },
        ]),
    };

    return metadata;
  }

  return __getRouterSitemap;
}

/**
 * Returns comprehensive page information for a specific route.
 * Called by the editor via POST with flat body + query params.
 */
export function getRouterPages(payload: object): RequestOptionsFactory<any> {
  function __getRouterPages({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.dynamic_page_router.v1.page',
      method: 'POST' as any,
      methodFqn: 'wix.dynamic_page_router.v1.DynamicPageRouter.GetRouterPages',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixDynamicPageRouterV1DynamicPageRouterUrl({
        protoPath: '/v1/pages',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __getRouterPages;
}

/**
 * Returns comprehensive page information for a specific route (cacheable).
 * Called by the viewer via GET with .r parameter containing {urlParams, body} envelope.
 */
export function getRouterPagesCached(
  payload: object
): RequestOptionsFactory<any> {
  function __getRouterPagesCached({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.dynamic_page_router.v1.page',
      method: 'GET' as any,
      methodFqn:
        'wix.dynamic_page_router.v1.DynamicPageRouter.GetRouterPagesCached',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixDynamicPageRouterV1DynamicPageRouterUrl({
        protoPath: '/v1/pages',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload, true),
    };

    return metadata;
  }

  return __getRouterPagesCached;
}

/** Returns the preview token used for draft collection previews. */
export function getDraftPreviewToken(
  payload: object
): RequestOptionsFactory<any> {
  function __getDraftPreviewToken({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.dynamic_page_router.v1.page',
      method: 'POST' as any,
      methodFqn:
        'wix.dynamic_page_router.v1.DynamicPageRouter.GetDraftPreviewToken',
      packageName: PACKAGE_NAME,
      migrationOptions: {
        optInTransformResponse: true,
      },
      url: resolveWixDynamicPageRouterV1DynamicPageRouterUrl({
        protoPath: '/v1/draft-preview-token',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __getDraftPreviewToken;
}
